import { useQuery } from '@apollo/client';
import useReportQueryParams from 'components/ReportsModule/hooks/use-report-query-params';
import { ReportEntityType } from 'components/ReportsModule/types';
import { baseWhere } from 'graph/apollo-util';
import GET_CURRICULUM_LESSON_REPORT from 'graph/reports/get-curriculum-lesson-report';
import GET_CURRICULUM_PROGRESS_REPORT from 'graph/reports/get-curriculum-progress-report';
import { sortBy } from 'lodash';
import React, { useMemo } from 'react';
import {
  Maybe,
  PrmCurriculumLessonReportResult,
  PrmCurriculumProgressReportResult
} from 'types/schema-generated-types';

const useSkillProgress = () => {
  const [productCurriculumId, setSelectedProductCurriculumId] = React.useState<Maybe<number>>(null);

  const [{ entityId, entityType }] = useReportQueryParams();
  const baseInput = {
    organizationId: entityId,
    ...(productCurriculumId && { productCurriculumId })
  };

  const curriculumProgressInputMap: any = {
    [ReportEntityType.CLASSROOM]: {
      classroomId: entityId
    },
    [ReportEntityType.DISTRICT]: { organizationId: entityId },
    [ReportEntityType.SCHOOL]: { organizationId: entityId },
    [ReportEntityType.USER_GROUP]: { organizationId: entityId },
    [ReportEntityType.STUDENT]: baseInput,
    [ReportEntityType.USER]: {
      userId: entityId
    }
  };
  const curriculumLessonInputMap: any = {
    [ReportEntityType.DISTRICT]: { organizationId: entityId, productCurriculumId },
    [ReportEntityType.CLASSROOM]: {
      classroomId: entityId,
      productCurriculumId
    },
    [ReportEntityType.SCHOOL]: { organizationId: entityId, productCurriculumId },
    [ReportEntityType.USER_GROUP]: { organizationId: entityId, productCurriculumId },
    [ReportEntityType.STUDENT]: {
      studentId: entityId,
      ...(productCurriculumId && { productCurriculumId })
    },
    [ReportEntityType.USER]: {
      userId: entityId,
      productCurriculumId
    }
  };

  const maybeSkip = () => {
    switch (entityType) {
      case ReportEntityType.USER:
      case ReportEntityType.USER_GROUP:
        return !productCurriculumId;
      case ReportEntityType.DISTRICT:
      case ReportEntityType.SCHOOL:
        return !productCurriculumId;
      case ReportEntityType.STUDENT:
      case ReportEntityType.CLASSROOM:
        return !productCurriculumId;
      default:
        return false;
    }
  };

  const { data: progressReportData, loading: curriculumProgressLoading } = useQuery<{
    prmCurriculumProgressReport?: PrmCurriculumProgressReportResult;
  }>(GET_CURRICULUM_PROGRESS_REPORT, baseWhere(curriculumProgressInputMap?.[entityType]));

  const { data: curriculumLessonReport, loading: curriculumLessonLoading } = useQuery<{
    prmCurriculumLessonReport: PrmCurriculumLessonReportResult;
  }>(GET_CURRICULUM_LESSON_REPORT, {
    ...baseWhere(curriculumLessonInputMap?.[entityType]),
    skip: maybeSkip(),
    fetchPolicy: 'network-only'
  });

  const prmCurriculumProgressReport = progressReportData?.prmCurriculumProgressReport;

  const setList = useMemo(
    () =>
      sortBy(
        prmCurriculumProgressReport?.curriculums?.map(x => x.productCurriculum),
        o => o?.name
      ) ?? ([{ name: '', id: '' }] as any),
    // eslint-disable-next-line
    [prmCurriculumProgressReport?.curriculums]
  );

  return [
    {
      setList: setList as Array<{ name: string; id: number }>,
      selectedProductCurriculumId: productCurriculumId,
      prmCurriculumProgressReport: progressReportData?.prmCurriculumProgressReport,
      curriculumLessonReport: curriculumLessonReport?.prmCurriculumLessonReport,
      curriculumLessonLoading,
      curriculumProgressLoading
    },
    {
      setSelectedProductCurriculumId
    }
  ];
};

export default useSkillProgress;
